/**
 * Live runtime stats — backend-driven via Prometheus.
 *
 * Both hooks query the controller's `/metrics/*` endpoints (which
 * proxy PromQL queries to the deployed Prometheus). When Prometheus
 * is empty (e.g. local dev without an observability stack) the
 * endpoints return zeros and the dashboards render a flat sparkline
 * — no synthetic numbers.
 *
 * The names + return shapes match the previous synthetic hooks so
 * existing call sites don't need to change.
 */
import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";

export interface EdgeStats {
  envoy_replicas: number;
  envoy_replicas_target: number;
  rps: number;
  rps_history: number[];
  p50_ms: number;
  p99_ms: number;
}

const EMPTY_EDGE: EdgeStats = {
  envoy_replicas: 0,
  envoy_replicas_target: 0,
  rps: 0,
  rps_history: [],
  p50_ms: 0,
  p99_ms: 0,
};

/**
 * Global edge proxy stats. Polls every `intervalMs` (default 4s) so the
 * dashboard sparkline stays roughly live without hammering Prometheus.
 */
export function useEdgeStats(intervalMs = 4_000): EdgeStats {
  const q = useQuery({
    queryKey: ["metrics", "edge"],
    queryFn: () => api.get<EdgeStats>("/metrics/edge"),
    refetchInterval: intervalMs,
    staleTime: intervalMs / 2,
  });
  return q.data ?? EMPTY_EDGE;
}

/**
 * Workload-neutral per-replica snapshot. GPU / vLLM-specific tiles
 * (`gpu_util_pct`, `gpu_mem_*`, `tokens_per_sec`) were dropped here on
 * purpose — backstage runs arbitrary HTTP workloads (vLLM, TGI, echo,
 * custom Go, ...) and reading 0 on a CPU echo pool is worse than not
 * showing the tile at all. Operators who run a uniform vLLM fleet can
 * still scrape those metrics off the same Prometheus into whichever
 * external dashboard tool they prefer.
 */
export interface PodMetrics {
  health: "ok" | "degraded" | "down" | "unknown";
  last_health_at: string;
  uptime_seconds: number;
  rps: number;
  rps_history: number[];
  queue_depth_ms: number;
}

interface ReplicaMetricsWire {
  health: string;
  last_health_at: string | null;
  uptime_seconds: number;
  rps: number;
  rps_history: number[];
  queue_depth_ms: number;
}

const EMPTY_POD: PodMetrics = {
  health: "unknown",
  last_health_at: "",
  uptime_seconds: 0,
  rps: 0,
  rps_history: [],
  queue_depth_ms: 0,
};

function normalizeHealth(s: string): PodMetrics["health"] {
  return s === "ok" || s === "degraded" || s === "down" ? s : "unknown";
}

/**
 * Per-replica live metrics. `replicaId` should be the DB row id
 * (`Replica.id`); the controller looks up the corresponding Prometheus
 * series by `replica_id` label. Returns the empty snapshot until the
 * first response lands so consumers don't have to null-check.
 */
export function useReplicaMetrics(
  replicaId: number | null | undefined,
  intervalMs = 4_000,
): PodMetrics {
  const enabled = typeof replicaId === "number" && replicaId > 0;
  const q = useQuery({
    queryKey: ["metrics", "replica", replicaId],
    queryFn: async () => {
      const wire = await api.get<ReplicaMetricsWire>(
        `/metrics/replicas/${replicaId}`,
      );
      const out: PodMetrics = {
        health: normalizeHealth(wire.health),
        last_health_at: wire.last_health_at ?? "",
        uptime_seconds: wire.uptime_seconds,
        rps: wire.rps,
        rps_history: wire.rps_history,
        queue_depth_ms: wire.queue_depth_ms,
      };
      return out;
    },
    enabled,
    refetchInterval: enabled ? intervalMs : false,
    staleTime: intervalMs / 2,
  });
  return q.data ?? EMPTY_POD;
}

/**
 * Legacy alias — older call sites pass an arbitrary string seed
 * (e.g. the pod's UUID) and don't have the replica id handy. Kept as
 * a thin shim that returns the empty snapshot until they're migrated
 * to `useReplicaMetrics`. The parameter is intentionally unused.
 */
export function usePodMetrics(_seedKey: string, _intervalMs = 4_000): PodMetrics {
  return EMPTY_POD;
}
